import React, { useState, forwardRef, useImperativeHandle, useEffect, useMemo } from 'react'
import { Form, Row, Col, Collapse, Tooltip, Descriptions, Table, Button, Space, Message, Tabs, Divider, Popover, Modal, Dropdown, Menu, Tag } from '@bedrock/components'
import { Delete, PlusCircle, Plus, Copy2, NoticeTextRight, ChevronDown, SignIn, SignOut, Download, CopyNewAdd } from '@bedrock/icons-react'
import { isEqual } from "lodash";
import { useControllableValue, useMemoizedFn } from "ahooks";
import classNames from "classnames/bind";
import styles from "./index.less";
import KPITable, { KPITableProps } from "#features/KPITable";
import SFromItem from "#components/SForm/SFormItem";
import CollapseTablePanel from "#components/CollapseTablePanel";
import FormItemDescriptions, {
    FormItemDescriptionsItemType
} from "#components/DescriptionsFormItem";
import {
    commonGetDemandCooperateProperties,
    PropertyRefDto,
    DemandCooperatePropertyValueDto,
    DemandKpiValueDto,
    commonFilterOptions,
    commonGetDemandCooperateProperties2,
    DemandCooperatePropertiesVo
} from "@mpc/common/src/services/api";
import { useImportFileModal } from "#features/importFileModal";
import CooperationPropertyForm, { alignPropertyValues } from "#features/CooperationFormList/CooperationPropertyForm";
import OvertimeTag from '#features/OvertimeTag';
import { orderNum } from "#utils/number";
import ActualExecutionTag from '#features/ActualExecutionTag';
import PriceDetailModal from './PriceDetailModal';
import CooperationDetails from './CooperationDetails';
import CurrencyType from '#components/SForm/SFormElement/CurrencyType';
import { $sType } from '#utils';
import EquityFormTable, { getExpectRightsLabel, getFormItemKey } from '@mpc/common/src/features/EquityFormTable';
import { cloneDeep } from 'lodash';
import TravelTag from '#features/TravelTag';
import { getInnerStructuredSersion } from '#features/utils';
import ClosingReportBtn from '#features/MPCDemand/ClosingReportTable/ClosingReportBtn';
import NoStructuredCooperation from './NoStructuredCooperation';
import ExportFilePollingBtn from '#features/ExportFilePollingBtn';
import { px2rem } from "@bedrock/utils";
import { ProForm, ProRadio, ProSelect } from '@bedrock/eed-react';
import StarSelectDrawer from '#features/MPCDemand/StarSelectDrawer';
import { withFunctionCall } from '@mpc/components';
import { ALLOW_SELECT_KOL_CATEGORY_ID, getCooperateAIRequired } from '#utils/CONST';
import AuditException from '#features/AuditException';
import { bindAiReviewResultToFormItem } from '#features/MPCDemand';

const cx = classNames.bind(styles)

const DEFAULT_ACTIVE_KEYS = Array.from({length: 20}, (_, i) => i);
const ALL_ACTIVE_KEYS = Array.from({length: 1000}, (_, i) => i);

export type CooperationValueItem = {
    id?: number;
    /** 备注 */
    remark?: string;
    /** 是否实际执行 */
    need_actual_execution: boolean;
    /** 【结构化】合作内容属性值 */
    property_values?: DemandCooperatePropertyValueDto[];
    /** 【结构化】kpi指标列表 */
    kpi_values?: DemandKpiValueDto[];
    /** 是否包含含差旅餐饮等实报实销 */
    contain_travel_expenses_reimbursement?: boolean;
}

type CooperationFormListProps = {
    category_id: number; // 需求ID
    hasKPI?: boolean; // 是否有KPI
    view?: boolean; // 是否是编辑类型
    value?: CooperationValueItem[];
    onChange?: (value: any[]) => void;
    kpiTableProps?: KPITableProps; // KPI属性
    depth?: number;
    inPlanning: boolean; // 是否为策划信息
    addable?: boolean; // 是否可添加
    deletable?: boolean; // 是否可删除
    readonly?: boolean; // 是否只读,强制所有字段只读，不分场景
    /**
     * 编辑类型
     * default: 默认编辑
     * returnToBusiness: 退回商务确认报价节点（策划编辑）
     */
    editType?: 'default' | 'returnToBusiness'; // 编辑类型
    demand_supplier_quotes?: any
    detail?: any;
    onImportSuccess?: () => void;
    schema?: any
    tabsModel?: boolean; // 是否是tabs模式
    updateReleaseTime?: (values: any, add?: boolean) => void; // 更新发布时间
}

    
// 下拉多选字段，应用到整单

type DropdownListApplyToTheWholeOrderProps = {
    hideOptions?: {
        name: string;
        hide: boolean;
    }[];
    schema: any,
    onOk: (data: ApplyToTheWholeOrderData) => void;
}

type ApplyToTheWholeOrderData = {
    // 覆盖模式：保留原值或者覆盖原值
    coverMode: 'retain' | 'overwrite';
    // 覆盖字段
    coverKeys: string[];
}

const getCoverByCoverMode = (coverMode: 'retain' | 'overwrite', coverValue: any, originalValue: any, isEmpty = false) => {
    if (coverMode === 'overwrite') {
        return coverValue;
    }
    // 判断如果原值为空，或者空数组，retain模式下也覆盖
    if (isEmpty || originalValue === undefined 
        || originalValue === null 
        || originalValue === '' 
        || (Array.isArray(originalValue) && originalValue.length === 0)
        || (Array.isArray(originalValue) && JSON.stringify(originalValue) === '[{}]') // 针对kpi数据，可能为空数组，也可能是[{}]
    ) {
        return coverValue;
    }
    return originalValue;
}


// 下拉多选字段，应用到整单
const DropdownListApplyToTheWholeOrder = (props: DropdownListApplyToTheWholeOrderProps) => {
    const { schema, onOk, hideOptions = [] } = props;
    const [visible, setVisible] = React.useState(false);
    const { viewBase } = React.useContext(px2rem);
    const [form] = ProForm.useForm();
    const widthStyle = {
        width: 280 * viewBase,
    };

    const handleOk = () => {
        form.validateFields().then((values) => {
            onOk?.(values);
            setVisible(false);
        });
    }
    const handleCancel = () => {
        setVisible(false);
    }

    const KeysOptions = useMemo(() => {
        const propertyValuesTemplate = alignPropertyValues([], schema)
        return [
            { name: '全选', value: '__SELECT_ALL__' },
            ...propertyValuesTemplate?.map?.((item: any) => {
                return {
                    name: item.label,
                    value: `property_values.${item.property_code}`,
                }
            }),
            { name: '考核指标', value: 'kpi_values' },
            { name: '必要权益', value: 'necessary_rights' },
            { name: '期望权益', value: 'expect_rights' },
            { name: '备注', value: 'remark' },
        ].filter((item) => !hideOptions.some((hideOption) => hideOption.name === item.value && hideOption.hide))
    }, [schema, hideOptions])

    useEffect(() => {
        if (visible) {
            form.setFieldsValue({
                coverMode: 'retain',
                coverKeys: KeysOptions.map((item) => item.value),
            });
        }
    }, [visible, KeysOptions])


    const content = (
        <Space direction="vertical" size={8} style={widthStyle} onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
        }}>
            <ProForm
                form={form}
                submitter={false}
                initialValues={{
                    coverMode: 'retain',
                    coverKeys: ['__SELECT_ALL__'],
                }}
            >
                <ProRadio.Group
                    name="coverMode"
                    label="有内容时是否批量覆盖"
                    fieldProps={{
                        options: [
                            { name: '保留原值', value: 'retain' },
                            { name: '覆盖原值', value: 'overwrite' },
                        ]
                    }}
                />
                <ProSelect
                    required
                    multiple
                    name="coverKeys"
                    label="选择应用至整单的字段"
                    fieldProps={{
                        multiple: true,
                        options: KeysOptions,
                        showSelectAllOption: true,
                        allDisplay: 'options',
                        returnOnlyLeaf: true,
                        type: 'label',
                        maxTagCount: 'responsive',
                        showConfirm: true,
                        showReset: true,
                    }}
                />
            </ProForm>
        <Space className="justify-end" style={widthStyle}>
            <Button onClick={handleCancel} size="small">取消</Button>
            <Button onClick={handleOk} type="primary" size="small">
                确定
            </Button>
        </Space>
        </Space>
    );
    return (
        <Popover
            content={content}
            trigger="click"
            visible={visible}
            onVisibleChange={setVisible}
            showArrow={false}
        >
            <Button onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                setVisible(true)
            }} icon={<Copy2 />} type="text-subtle" size="xSmall" suffixIcon={<ChevronDown />}>字段应用至整单</Button>
        </Popover>
    );
};


type ImportMode = 'overwrite' | 'append';

const CooperationFormList = forwardRef(function (props: CooperationFormListProps, ref) {
    const [dataList, setDataList] = useControllableValue<CooperationValueItem[]>(props, {
        defaultValue: []
    })
    const {
        category_id,
        view = false,
        hasKPI,
        kpiTableProps = {},
        depth, addable = true,
        deletable = true,
        editType = 'default',
        demand_supplier_quotes = [],
        detail,
        onImportSuccess,
        schema,
        tabsModel,
        updateReleaseTime,
        readonly = false,
    } = props
    const [activeKeys, setActiveKeys] = useState<number[]>(tabsModel ? ALL_ACTIVE_KEYS : DEFAULT_ACTIVE_KEYS);
    const [activeTabKey, setActiveTabKey] = useState('0');

    const [form] = Form.useForm();

    // 外部数据变化，更新本地数据
    const [localDataList, setLocalDataList] = useState([])
    useEffect(() => {
        if (isEqual(dataList, localDataList)) {
            return
        } else {
            form.setFieldsValue({
                list: dataList
            })
        }
    }, [dataList, localDataList]);

    useImperativeHandle(ref, () => ({
        validateFields: () => {
            // 缓执行，解决动态校验逻辑，导致新规则无法及时生效问题
            return new Promise((resolve, reject) => {
                setTimeout(() => {
                    form.clearValidate();
                    form.validateFields()
                        .then(() => {
                            resolve(undefined)
                        })
                        .catch((error) => {
                            return reject(undefined)
                        })
                }, 10)
            })
        },
    }))


    const { openImportFileModal, closeImportFileModal } = useImportFileModal({});

    const handleImportCooperate = useMemoizedFn((mode: ImportMode) => {
        const importType = `demand_cooperate_planner_${category_id}`;
        openImportFileModal({
            importType,
            importIsMemory: false,
            customDownloadRender: () => (
                <div>
                    <Space size='xSmall' split={<Divider />}>
                        <ExportFilePollingBtn
                            exportKey='DEMANDS_EXPORT_COOPERATE_DATA'
                            params={{
                                l3_category_id: category_id,
                                cooperate_account_dto_list: dataList?.map?.((item, index) => {
                                    return { ...item, index_id: item?.index_id ?? index + 1 }
                                }),
                            }}
                            buttonProps={{
                                icon: <SignOut />,
                                type: 'link',
                            }}
                            buttonText='导出当前数据'
                        />
                        <Button
                            icon={<Download />}
                            type='link'
                            onClick={() => {
                                window.open(`/api/v2/java_proxy/common/data/${importType}/import-template`)
                            }}
                        >
                            下载空模板
                        </Button>
                    </Space>
                    <div className="download-span fs-12 m-l-4">
                        (上传前请先下载Excel模板并按照模板格式编辑文件，或先导出当前数据后再导入)
                    </div>
                </div>
            ),
            uploadFinishedCallback: (res) => {
                // 有错误时，不进行数据更新
                if (res?.data?.fail_count > 0) return;
                if (res.code === 1 && res.data?.data?.length) {
                    closeImportFileModal();
                    const list = res.data?.data?.map(v => v?.demand_cooperate_account_dto);
                    if (mode === 'overwrite') {
                        Message.success(`导入覆盖成功 ${list.length} 条数据`)
                        setDataList(list)
                    } else {
                        Message.success(`导入添加成功 ${list.length} 条数据`)
                        setDataList([...dataList, ...list])
                    }
                    onImportSuccess?.();
                }
            },
        })
    })

    const onFormChange = useMemoizedFn((changedValues, allValues) => {
        setDataList(allValues.list)
        setLocalDataList(allValues.list)
    })
    const isSingle = dataList?.length <= 1;

    const handleApplyToTheWholeOrder = (key, value) => {
        const newDataList = dataList.map((item, index) => ({
            ...item,
            [key]: value
        }))
        setDataList(newDataList);
        Message.success('应用成功，已覆盖填写到全部合作内容')
    }



    // 选择字段应用至整单
    const handleApplyValuesToTheWholeOrder = useMemoizedFn(({
        coverMode,
        coverKeys,
        currentIndex,
    }: {
        coverMode: 'retain' | 'overwrite',
        coverKeys: string[],
        currentIndex: number,
    }) => {
        // 获取当前合作内容的值，基于coverMode 重置整个合作内容list
        const currentValue = dataList[currentIndex];
        // property_values注意要基于property_values.property_code 取值进行覆盖
        const coverMap = coverKeys.reduce((result, key) => {
            result[key] = true;
            return result;
        }, {});

        const newDataList = dataList.map((item: Record<string, any>, index) => {
            if (index !== currentIndex) {
                return Object.keys(currentValue).reduce((result: Record<string, any>, key) => {
                    if(!coverMap[key] && key !== 'property_values') {
                        result[key] = item[key];
                        return result;
                    }
                    switch (key) {
                        case 'property_values':
                            result['property_values'] = item['property_values'].map((propertyValue: DemandCooperatePropertyValueDto) => {
                                if (coverMap[`property_values.${propertyValue.property_code}`]) {
                                    const coverValue = currentValue['property_values'].find((v: DemandCooperatePropertyValueDto) => v.property_code === propertyValue.property_code)?.value;
                                    return {
                                        ...propertyValue,
                                        value: coverMap[`property_values.${propertyValue.property_code}`] ? getCoverByCoverMode(coverMode, coverValue, propertyValue.value) : propertyValue.value,
                                    }
                                } else {
                                    return propertyValue;
                                }
                            });
                            // 二询草稿单场景下可能存在
                            currentValue['property_values'].forEach((coverPropertyValue: DemandCooperatePropertyValueDto) => {
                                // 如果result['property_values']中没有该property_code，则添加
                                if (!result['property_values'].some((v: DemandCooperatePropertyValueDto) => v.property_code === coverPropertyValue.property_code)) {
                                    result['property_values'].push({
                                        ...coverPropertyValue,
                                        value: coverMap[`property_values.${coverPropertyValue.property_code}`] ? getCoverByCoverMode(coverMode, [], coverPropertyValue.value) : [],
                                    });
                                }
                            });
                            break;
                        case 'kpi_values':
                            // 针对kpi数据，可能为空数组, 也可能是初始化的值
                            let isEmpty = false;
                            try {
                                isEmpty = JSON.stringify(item[key]) === '[{"accept_flag":false,"custom":0}]';
                            } catch (error) {
                                isEmpty = false;
                            }
                            result[key] = getCoverByCoverMode(coverMode, currentValue[key], item[key], isEmpty);
                            break;
                        default:
                            result[key] = getCoverByCoverMode(coverMode, currentValue[key], item[key]);
                            break;
                    }
                    return result;
                }, item);
            }
            return item
        })

        setDataList(newDataList as CooperationValueItem[]);
        Message.success('应用成功')
    })

    return (<Form
        className={cx('mpc-common-cooperate-form-list')}
        form={form}
        onValuesChange={onFormChange}
        initialValues={{
            list: [{}]
        }}
        style={{ width: '100%' }}
    >
        <Form.List
            name="list"
        >
            {(fields, { add, remove }, { errors }) => {
                const hasError = errors && !!errors.length;
                return (
                    <>
                      {!view && addable && detail?.structured && <div style={{ position: 'relative' }}>
                        <Space className={cx('import-file-btn')} size={8}>
                            {ALLOW_SELECT_KOL_CATEGORY_ID.includes(category_id?.toString()) && (
                                <Dropdown
                                    overlay={(
                                        <Menu
                                            onClick={({ key }) => {
                                                const mode = key as ImportMode;
                                                StarSelectDrawer.show({
                                                    dataType: 'cooperate',
                                                    l3_category_id: category_id,
                                                    manualAddable: false,
                                                    title: '选择在库达人',
                                                    onOk: ({ contentData }) => {
                                                        if (contentData?.length) {
                                                            if (mode === 'overwrite') {
                                                                setDataList(contentData);
                                                                Message.success('达人覆盖添加成功，已清空原数据并导入新达人')
                                                            } else {
                                                                if (dataList.length === 1 && dataList[0]?.property_values?.every((item) => !item.value || item?.value?.length === 0)) {
                                                                    setDataList(contentData);
                                                                } else {
                                                                    setDataList([...dataList, ...contentData]);
                                                                }
                                                                Message.success('达人添加成功，可通过「批量导入」/「字段应用至整单」功能快速补充剩余字段信息')
                                                            }
                                                        } else {
                                                            Message.info('没有有效数据')
                                                        }
                                                    }
                                                })
                                            }}
                                        >
                                            <Menu.Item key="overwrite">
                                                清空覆盖导入
                                            </Menu.Item>
                                            <Menu.Item key="append">
                                                添加导入
                                            </Menu.Item>
                                        </Menu>
                                    )}
                                >
                                    <Button
                                        size='medium'
                                        type="checked-neutral"
                                        icon={<Plus />}
                                        suffixIcon={<ChevronDown className="rock-dropdown-down-arrow-icon" />}
                                    >
                                        添加达人
                                    </Button>
                                </Dropdown>
                            )}
                            <Dropdown
                                overlay={(
                                    <Menu
                                        onClick={({ key }) => handleImportCooperate(key as ImportMode)}
                                    >
                                        <Menu.Item key="overwrite">
                                          清空覆盖导入
                                        </Menu.Item>
                                        <Menu.Item key="append">
                                           添加导入
                                        </Menu.Item>
                                    </Menu>
                                )}
                            >
                                <Button
                                    type="checked-neutral"
                                    icon={<SignIn />}
                                    suffixIcon={<ChevronDown className="rock-dropdown-down-arrow-icon" />}
                                >
                                    批量导入合作内容
                                </Button>
                            </Dropdown>
                        </Space>      
                      </div>}
                        {tabsModel && <Tabs activeKey={activeTabKey} type="card" onChange={(v) => {setActiveTabKey(v)}} className='m-b-8'>
                            {
                                fields.map((field, index) => {
                                    return <Tabs.TabPane tab={`合作内容${orderNum(dataList?.[index]?.$orderIndex, index)}`} key={index + ''}></Tabs.TabPane>
                                })
                            }
                        </Tabs>}
                        {fields.map((field, index) => {
                            const { key, name, ...fieldRest } = field;
                            const fieldConfig = (fieldType: string) => {
                                return {
                                    formItemKey: [name, fieldType],
                                    name: [name, fieldType],
                                    fieldKey: [key, fieldType],
                                    view,
                                    ...fieldRest
                                }
                            }

                            const { delay_grade, delay_label, $orderIndex, index_id } = dataList?.[index] || {};
                            const cooperate_account_id = dataList?.[index]?.id;
                            const demand_supplier_quote = demand_supplier_quotes?.find?.(v => v.cooperate_account_id === cooperate_account_id);
                            const title = `合作内容${orderNum($orderIndex, index)}`

                          if (tabsModel && index !== Number(activeTabKey)) {
                                return null;
                            }


                            return (
                                <Collapse
                                    // defaultActiveKey={[1]}
                                    activeKey={activeKeys.includes(index) ? [1] : undefined}
                                    key={index}
                                    onChange={(key) => {
                                        setActiveKeys(activeKeys.includes(index) ? activeKeys.filter((i) => i !== index) : [...activeKeys, index])
                                    }}
                                >
                                    <CollapseTablePanel
                                        className={OvertimeTag.conf?.[delay_grade]?.panelHeaderbgClassName}
                                        // showArrow={false}
                                        style={{ width: '100%' }}
                                        header={<div
                                            className={cx('inner-header')}
                                        >
                                            <div>
                                                <Space size={4}>
                                                    <span>{title}</span>
                                                    {dataList[index]?.need_ai && <Tag className='font-normal' color="orange" size="xSmall" text={'需包含AI技术'}></Tag>}
                                                     {depth === 2 && !props.inPlanning && <ActualExecutionTag value={dataList[index]?.need_actual_execution} />}
                                                    {!props.inPlanning && <TravelTag
                                                        value={demand_supplier_quote?.contain_travel_expenses_reimbursement}
                                                        need_actual_execution={demand_supplier_quote?.need_actual_execution}
                                                        detail={detail}
                                                    />}
                                                    {!props.inPlanning && <OvertimeTag delay_grade={delay_grade} delay_label={delay_label} className='' />}
                                                </Space>
                                            </div>
                                            {!view && <Space
                                                size={0}
                                            >
                                                {
                                                    addable && detail?.structured && 
                                                    <DropdownListApplyToTheWholeOrder
                                                        schema={schema}
                                                        hideOptions={[
                                                                {
                                                        name: 'kpi_values',
                                                        hide: !hasKPI || (detail?.need_cooperate_account_supplier_settle_price && dataList[index]?.need_actual_execution === false),
                                                            }, {
                                                        name: 'necessary_rights',
                                                    hide: detail?.structured_version < 1 && !['pending_design','underway'].includes(detail?.state),
                                                            }, {
                                                        name: 'expect_rights',
                                                    hide: detail?.structured_version < 1 && !['pending_design','underway'].includes(detail?.state),
                                                            }]}
                                                        onOk={(applyConfig) => handleApplyValuesToTheWholeOrder({
                                                            coverMode: applyConfig.coverMode,
                                                            coverKeys: applyConfig.coverKeys,
                                                            currentIndex: index,
                                                        })} />
                                                }
                                                {addable &&
                                                    <Button type="text-subtle" size="xSmall" icon={<CopyNewAdd />}
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                const listData = form.getFieldValue('list')
                                                                const copyData = JSON.parse(JSON.stringify(listData[index]))
                                                                add(copyData, index + 1)
                                                            }}
                                                    >
                                                        复制合作内容
                                                    </Button>
                                                }

                                                {deletable &&
                                                    <Button disabled={isSingle} type="text-subtle" size="xSmall" icon={<Delete />}
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                remove(field.name);
                                                            }}
                                                            danger
                                                    >
                                                            删除
                                                    </Button>
                                                }
                                            </Space>}
                                            {detail?.need_cooperate_account_supplier_settle_price && !props.inPlanning && <div>
                                                <span className={cx('settle-title')}>最终结算金额：</span>
                                                <CurrencyType value={dataList?.[index]?.$supplier_settle_price} view showAmountOrder={false} />
                                            </div>}
                                        </div>}
                                        key={1}>
                                        {!view && detail?.structured &&  getCooperateAIRequired(category_id) && <FormItemDescriptions
                                            lightBorder
                                            className={cx('need_ai_item')}
                                            column={2}
                                            items={[
                                                {
                                                    ...fieldConfig('need_ai'),
                                                    label: '是否需包含AI',
                                                    required: true,
                                                    view: false,
                                                    span: 2,
                                                    componentClass: 'radio',
                                                    elementProps: {
                                                        options: [
                                                            { name: '是', value: true },
                                                            { name: '否', value: false },
                                                        ],
                                                    },
                                                },
                                            ]}
                                        />}
                                        {!view && detail?.structured && <SFromItem
                                            {...fieldConfig('property_values')}
                                            showLabel={false}
                                            validateTrigger={false}
                                            watchChildFormValidate={true}
                                            className={cx('property_values_from_item')}
                                            render={() => {
                                                return <CooperationPropertyForm
                                                    view={view}
                                                    schema={schema}
                                                    editType={props.editType}
                                                    structuredVersion={getInnerStructuredSersion(detail, view)}
                                                />
                                            }}
                                        />}
                                        {detail?.structured && <FormItemDescriptions
                                            lightBorder
                                            className={cx('collapse-content-table')}
                                            column={2}
                                            items={[
                                                {
                                                    ...fieldConfig('property_values'),
                                                    label: '合作内容',
                                                    validateTrigger:false,
                                                    watchChildFormValidate: true,
                                                    span: 2,
                                                    hide: !view || !dataList?.[index]?.property_values?.length,
                                                    render:() => {
                                                        return <CooperationDetails index_id={orderNum($orderIndex, index)} need_ai={dataList?.[index]?.need_ai}/>
                                                    }
                                                },
                                                {
                                                    ...fieldConfig('kpi_values'),
                                                    label: '考核指标',
                                                    view: readonly ? true : (() => {
                                                        // 判断是否有编辑权限,单个编辑，否则无法校验
                                                        const hasColEdit = kpiTableProps?.colKeys?.some?.((n) => n.view === false)
                                                        if (hasColEdit) return false;
                                                        return view
                                                    })(),
                                                    required: !!kpiTableProps?.required,
                                                    span: 2,
                                                    watchChildFormValidate: true,
                                                    validateTrigger: false,
                                                    hide: !hasKPI || (detail?.need_cooperate_account_supplier_settle_price && dataList[index]?.need_actual_execution === false),
                                                    render: (renderData, itemProps) => {
                                                        // 是否实际执行为否则KPI不可编辑
                                                        const kpiView = dataList[index]?.need_actual_execution === false ? true : (view || readonly);
                                                        return <KPITable
                                                            need_actual_execution={dataList[index]?.need_actual_execution}
                                                            view={kpiView}
                                                            required={false}
                                                            platforms={['MPC']}
                                                            emptyRender={() => {
                                                                return <div>--</div>
                                                            }}
                                                            $supplier_settle_price={dataList[index]?.$supplier_settle_price}
                                                            cooperate_account_id={cooperate_account_id}
                                                            need_cooperate_account_supplier_settle_price={detail?.need_cooperate_account_supplier_settle_price}
                                                            applyToTheWholeOrder={!view && addable && detail?.structured}
                                                            handleApplyToTheWholeOrder={(value) => handleApplyToTheWholeOrder('kpi_values', value)}
                                                            {...kpiTableProps}
                                                        />
                                                    }
                                                },
                                                {
                                                    ...fieldConfig('necessary_rights'),
                                                    label: '必要权益',
                                                    span: 2,
                                                    hide: detail?.structured_version < 1 && !['pending_design','underway'].includes(detail?.state),
                                                    required: true,
                                                    watchChildFormValidate: true,
                                                    validateTrigger: false,
                                                    render: () => {
                                                        return <EquityFormTable
                                                            key='necessary_rights' 
                                                            formItemKey='necessary_rights' 
                                                            detail={detail} 
                                                            view={view || readonly} 
                                                            inPlanning={props.inPlanning}
                                                            rightsList={schema?.necessary_rights_list}
                                                            applyToTheWholeOrder={!view && addable && detail?.structured}
                                                            handleApplyToTheWholeOrder={(value) => handleApplyToTheWholeOrder('necessary_rights', value)}
                                                        />
                                                    }
                                                },
                                                {
                                                    ...fieldConfig(getFormItemKey(detail, {inPlanning: props.inPlanning, view})),
                                                    label: getExpectRightsLabel(getFormItemKey(detail, {inPlanning: props.inPlanning, view})),
                                                    span: 2,
                                                    hide: detail?.structured_version < 1 && !['pending_design','underway'].includes(detail?.state),
                                                    watchChildFormValidate: true,
                                                    validateTrigger: false,
                                                    render: () => {
                                                        return <EquityFormTable
                                                                key='expect_rights'
                                                                formItemKey={getFormItemKey(detail, {inPlanning: props.inPlanning, view})}
                                                                detail={detail}
                                                                inPlanning={props.inPlanning}
                                                                view={view || readonly}
                                                                tableRequired={false}
                                                                rightsList={schema?.expect_rights_list}
                                                                applyToTheWholeOrder={!view && addable && detail?.structured}
                                                                handleApplyToTheWholeOrder={(value) => handleApplyToTheWholeOrder(getFormItemKey(detail, {inPlanning: props.inPlanning, view}), value)}
                                                            />
                                                    }
                                                },
                                                {
                                                    ...fieldConfig('remark'),
                                                    label: '备注',
                                                    required: false,
                                                    componentClass: "textarea",
                                                    span: 2,
                                                    elementProps: {
                                                        maxLength: 500,
                                                    }
                                                },
                                                {
                                                    ...fieldConfig('discount_quote_price_with_tax'),
                                                    label: '报价金额-变更后(含税 + 服务费)',
                                                    span: 1,
                                                    hide: !detail?.need_cooperate_account_supplier_settle_price || props.inPlanning,
                                                    render: () => {
                                                        return <PriceDetailModal
                                                            demand_supplier_quote={demand_supplier_quote}
                                                            detail={detail}
                                                            orderNum={orderNum($orderIndex, index)}
                                                        />
                                                    }
                                                },
                                                {
                                                    ...fieldConfig('$supplier_settle_price'),
                                                    label: '最终结算金额(含税 + 服务费)',
                                                    span: 1,
                                                    componentClass: 'currencyType',
                                                    view: $sType === 'inner' || readonly ||
                                                     dataList[index]?.need_actual_execution === false,
                                                    required: true,
                                                    hide: !detail?.need_cooperate_account_supplier_settle_price || props.inPlanning,
                                                    elementProps: {
                                                        selectDisabled: true,
                                                        // max: demand_supplier_quote?.discount_quote_price_with_tax,
                                                        // formatter: undefined,
                                                    },
                                                    rules:
                                                        [
                                                            {
                                                                message: '金额需 ≤ 报价金额-变更后（含税+服务费）',
                                                                validator: function (_, value) {
                                                                    return new Promise((resolve, reject) => {
                                                                        if (value.value > demand_supplier_quote?.discount_quote_price_with_tax) {
                                                                            reject(new Error('金额需 ≤ 报价金额-变更后（含税+服务费）'));
                                                                        } else {
                                                                            resolve('');
                                                                        }
                                                                    });
                                                                },
                                                            }
                                                        ]

                                                },
                                                {
                                                    ...fieldConfig('case_close_detail'),
                                                    label: '结案明细',
                                                    span: 2,
                                                    view: true,
                                                    hide: !(detail?.need_structured_settle_report && (detail?.depth === 2) && demand_supplier_quote?.need_actual_execution && !props.inPlanning),
                                                    elementProps: ({ optionMap, detailData }) => {
                                                        return {
                                                            showComponent: (value) => {
                                                                return (
                                                                    <ClosingReportBtn detail={detail} view={view || readonly} cooperateAccountId={cooperate_account_id} />
                                                                )
                                                            }
                                                        }
                                                    },
                                                    ...bindAiReviewResultToFormItem(`quoteSettleReport:${index_id}`, {
                                                        detailData: detail,
                                                    }),
                                                },
                                                {
                                                    ...fieldConfig('case_close_report_cooperate_account_attachments'),
                                                    label: '结案报告 (差旅、餐补)',
                                                    span: 2,
                                                    required: !!demand_supplier_quote?.contain_travel_expenses_reimbursement,
                                                    view: readonly ? true : detail?.state !== 'making',
                                                    hide: (detail?.depth !== 2 || props.inPlanning || !detail?.$contentStructured || !detail?.is_screenshot_done)
                                                        ? true : !demand_supplier_quote?.need_actual_execution,
                                                    componentClass: 'upload',
                                                    inputExtra: !!demand_supplier_quote?.contain_travel_expenses_reimbursement && detail?.state === 'making' && (
                                                        <span className={cx('case-close-report-tip')}>注：当前合作内容包含差旅餐饮等结算项，需上传对应结案证明，以供实报实销结算。</span>
                                                    ),
                                                    ...bindAiReviewResultToFormItem(`caseCloseReportCooperateAccountAttachments:${index_id}`, {
                                                        detailData: detail,
                                                    }, { showGroups: true }),
                                                },
                                            ].filter(n => !n.hide)}
                                        />}
                                        {!detail?.structured && <NoStructuredCooperation fieldConfig={fieldConfig}/>}
                                    </CollapseTablePanel>
                                </Collapse>
                            );
                        })}
                        {!view && addable && <>
                            <Button type="checked" icon={<Plus />}
                                    onClick={() => {
                                        add({});
                                        setTimeout(() => {
                                            updateReleaseTime({} , true)
                                        }, 500);
                                    }}
                                    // style={{width: '100%'}}
                                    size='medium'
                            >
                                添加合作内容
                            </Button>
                        </>}
                    </>
                );
            }}
        </Form.List>
    </Form>)
})

export default CooperationFormList;
